import React, { useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  doc, 
  updateDoc, 
  addDoc, 
  serverTimestamp,
  onSnapshot,
  setDoc,
  writeBatch 
} from 'firebase/firestore';
import { db, getDocuments } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';
import { 
  Package, 
  RefreshCw, 
  AlertTriangle, 
  CheckCircle, 
  Download, 
  Upload,
  Calendar,
  TrendingUp,
  TrendingDown,
  Eye,
  Settings,
  Filter,
  Search,
  Truck,
  Clock
} from 'lucide-react';
import { 
  Inventory as EnhancedInventory,
  Product as EnhancedProduct,
  BeatLoadout,
  LoadoutAllocation,
  LegacyInventoryJournal as InventoryJournal,
  ZohoSyncStatus
} from '../types/inventory';
import { Vehicle, Warehouse } from '../types';
import { UnifiedInventoryTransactionService } from '../services/unifiedInventoryTransactions';

interface InventoryManagementProps {
  onBack: () => void;
}

const InventoryManagement: React.FC<InventoryManagementProps> = ({ onBack }) => {
  type RouteStatusType = 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled';
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview'>('overview');
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  
  // Data states
  const [inventory, setInventory] = useState<EnhancedInventory[]>([]);
  const [products, setProducts] = useState<EnhancedProduct[]>([]);
  const [journalEntries, setJournalEntries] = useState<InventoryJournal[]>([]);
  const [syncStatus, setSyncStatus] = useState<ZohoSyncStatus[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  
  // Filter states
  // Warehouse selection UI removed; salesman only sees their route inventory
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');

  // Route-based inventory states
  const [currentRouteId, setCurrentRouteId] = useState<string>('');
  const [currentRoute, setCurrentRoute] = useState<{ id: string; routeId: string; routeName: string } | null>(null);
  const [routeStates, setRouteStates] = useState<{[routeId: string]: RouteStatusType}>({});
  const [routeLoadData, setRouteLoadData] = useState<{[key: string]: {[productId: string]: number}}>({});
  const [routeIssuingAllocations, setRouteIssuingAllocations] = useState<{[routeId: string]: {[productId: string]: number}}>({});
  const [routeInventory, setRouteInventory] = useState<{[routeId: string]: {[productId: string]: number}}>({});
  const [routeProducts, setRouteProducts] = useState<{productId: string, productName: string, unit: string, category: string}[]>([]);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (currentRouteId && selectedDate) {
      loadRouteData();
    }
  }, [currentRouteId, selectedDate]);

  // Load initial data and resolve salesman's assigned route (route-first)
  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load warehouses, routes, and beats to find user's assigned route
      const [routesData, beatsData, productsData] = await Promise.all([
        getDocuments('routes'), 
        getDocuments('beats'),
        getDocuments('products')
      ]);
      
      // Get current user's active route from beats table
      const today = selectedDate;
      const userActiveBeat = beatsData.find((beat: any) => 
        beat.salesmanId === currentUser?.id && 
        beat.isActive && 
        beat.forDate === today
      );
      
      if (userActiveBeat && userActiveBeat.routeId) {
        // Resolve strictly via custom routeId
        const routeDoc = routesData.find((r: any) => r.routeId === userActiveBeat.routeId);
        if (!routeDoc) {
          console.error('Route not found for beat (routeId):', { routeId: userActiveBeat.routeId });
        } else if (!routeDoc.routeId) {
          console.error('Resolved route is missing routeId:', routeDoc);
        } else {
          const resolvedRouteId = routeDoc.routeId as string;
          const resolvedRouteName = routeDoc.name || routeDoc.routeName || resolvedRouteId;
          setCurrentRouteId(resolvedRouteId);
          setCurrentRoute({ id: routeDoc.id, routeId: resolvedRouteId, routeName: resolvedRouteName });
        }
      }
      
      // 🔧 FIX: Handle both product document ID and productId field mapping
      console.log('🔍 DEBUG: Raw products data sample:', productsData.slice(0, 2));
      
      const vehicleProductsData = productsData.map((product: any) => {
        // Try to get the correct product ID - could be document ID or productId field
        const productId = product.productId || product.id;
        const productName = product.name || product.productName || `Unknown Product (${productId})`;
        
        console.log('🔍 DEBUG: Product mapping:', {
          documentId: product.id,
          productId: product.productId,
          finalProductId: productId,
          productName: productName,
          rawProduct: product
        });
        
        return {
          productId: productId,
          productName: productName,
          unit: product.unit || 'KG',
          category: product.category || product.categoryName || 'General'
        };
      });
      
      console.log('🔍 DEBUG: Final vehicleProductsData:', vehicleProductsData);
  setRouteProducts(vehicleProductsData);
      
      // Load journal and sync data
      await Promise.all([
        loadJournalEntries(),
        loadSyncStatus()
      ]);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load route-specific data (adapted from LoadoutPage.tsx)
  const loadRouteData = async () => {
    if (!currentRouteId) return;
    
    try {
      await loadRouteStatuses();
    } catch (error) {
      console.error('Error loading vehicle data:', error);
    }
  };

  // Load route statuses (from LoadoutPage.tsx)
  const loadRouteStatuses = async () => {
    if (!currentRoute || !currentRoute.routeId) return;

    try {
      const today = selectedDate;

      // Listen to route_daily_status for this route (vehicleId holds routeId here)
      const statusQuery = query(
        collection(db, 'route_daily_status'),
        where('routeId', '==', currentRoute.routeId),
        where('date', '==', today)
      );

      // Set up real-time listener
  const unsubscribe = onSnapshot(statusQuery, async (snapshot) => {
    // Track previous route states to detect changes
    const previousStates = { ...routeStates };
        
    // Use a local status variable (statusNow) to avoid TS narrowing issues
  let statusNow: RouteStatusType = 'planning';
    let loadData: {[productId: string]: number} = {};
    let detailedAllocations: Array<{productId: string; issuedQty?: number; openingQty?: number; allocatedQty?: number}> = [];

        // Process vehicle status record
        if (snapshot.docs.length > 0) {
          const docSnap = snapshot.docs[0];
          const data = docSnap.data() as any; // RouteDailyStatus-like
          statusNow = (data.status as RouteStatusType) || 'planning';
          loadData = data.plannedLoads || {};
          detailedAllocations = Array.isArray(data.detailedAllocations) ? data.detailedAllocations : [];
        } else {
          // No route status yet → remain in planning
        }

        // Check if vehicle just transitioned to 'on_field' - explicit type assertion to fix TypeScript inference
        const previousStatus = previousStates[currentRoute.routeId];
        const currentStatus = statusNow;
        const hasTransitionedToOnField = currentStatus === 'on_field' && previousStatus !== 'on_field';

        setRouteStates({ [currentRoute.routeId]: statusNow });
        setRouteLoadData({ [currentRoute.routeId]: loadData });
        // Also precompute issuing allocations map for immediate UI during 'issuing'
        if (statusNow === 'issuing') {
          const map: {[productId: string]: number} = {};
          (detailedAllocations || []).forEach((a: any) => {
            const issued = (a?.issuedQty ?? a?.allocatedQty ?? 0) as number;
            const carry = Number(a?.openingQty ?? 0) || 0;
            const total = carry + issued;
            if (a?.productId && total > 0) map[a.productId] = total;
          });
          setRouteIssuingAllocations({ [currentRoute.routeId]: map });
        } else if (statusNow === 'planning') {
          // In planning, show opening (carry-forward) + planned, so driver sees total expected stock
          const totals: {[productId: string]: number} = {};
          // 1) start with carry-forward from detailedAllocations (openingQty)
          (detailedAllocations || []).forEach((a: any) => {
            const carry = Number(a?.openingQty ?? 0) || 0;
            if (carry > 0 && a?.productId) totals[a.productId] = (totals[a.productId] || 0) + carry;
          });
          // 2) add plannedLoads if present
          Object.entries(loadData || {}).forEach(([pid, qty]) => {
            const q = Number(qty) || 0;
            if (q > 0) totals[pid] = (totals[pid] || 0) + q;
          });
          setRouteIssuingAllocations({ [currentRoute.routeId]: totals });
        }

        // Load additional data based on status (route inventory + issuing allocations)
  await loadRouteInventoryData(statusNow);
  await loadRouteIssuingAllocations(statusNow);

        // Log transition for debugging
        if (hasTransitionedToOnField) {
          console.log('🔍 InventoryManagement: Vehicle transitioned to on_field, inventory data updated');
        }
      });

      return unsubscribe;

    } catch (error) {
      console.error('Error loading vehicle statuses:', error);
    }
  };

  // Load route inventory data (using routeId as warehouseId)
  const loadRouteInventoryData = async (vehicleStatus: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled') => {
  if (!currentRoute || !currentRoute.routeId) return;
    
    try {
      const vehicleInventoryData: {[productId: string]: number} = {};
      
      // For routes, read confirmed inventory from inventory where warehouseId === routeId for on_field/settled
      if (vehicleStatus === 'on_field' || vehicleStatus === 'settled') {
        console.log('🔍 Loading route inventory for routeId:', currentRoute.routeId, 'status:', vehicleStatus);
        
        const vehicleInvSnapshot = await getDocs(
          query(
            collection(db, 'inventory'),
            where('warehouseId', '==', currentRoute.routeId) // routeId (custom)
            // No date filter - inventory records are current status only
          )
        );
        
        console.log('🔍 Found route inventory records:', vehicleInvSnapshot.docs.length);
        
        vehicleInvSnapshot.docs.forEach(doc => {
          const invData = doc.data();
          console.log('🔍 Route inventory record:', {
            productId: invData.productId,
            productName: invData.productName,
            closingStock: invData.closingStock,
            warehouseId: invData.warehouseId
          });
          vehicleInventoryData[invData.productId] = invData.closingStock || 0;
        });
        
        console.log('🔍 Final routeInventoryData:', vehicleInventoryData);
      }
      
  setRouteInventory({ [currentRoute.routeId]: vehicleInventoryData });
      
    } catch (error) {
      console.error('Error loading vehicle inventory:', error);
    }
  };

  // Load issuing allocations for route from route_daily_status
  const loadRouteIssuingAllocations = async (vehicleStatus: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled') => {
  if (!currentRoute || !currentRoute.routeId) return;
    
    try {
      const beatLoadoutData: {[productId: string]: number} = {};
      
      if (vehicleStatus === 'issuing' || vehicleStatus === 'planning') {
        const statusQuery = query(
          collection(db, 'route_daily_status'),
          where('routeId', '==', currentRoute.routeId),
          where('date', '==', selectedDate),
          where('status', 'in', ['planning', 'issuing'])
        );

        const statusSnapshot = await getDocs(statusQuery);

        statusSnapshot.docs.forEach(doc => {
          const statusData = doc.data() as any;

          if (statusData.detailedAllocations && statusData.detailedAllocations.length > 0) {
            statusData.detailedAllocations.forEach((allocation: any) => {
              const carry = Number(allocation.openingQty ?? 0) || 0;
              const issued = Number(allocation.issuedQty ?? allocation.allocatedQty ?? 0) || 0;
              if (vehicleStatus === 'issuing') {
                const total = carry + issued;
                if (total > 0) beatLoadoutData[allocation.productId] = (beatLoadoutData[allocation.productId] || 0) + total;
              } else if (vehicleStatus === 'planning') {
                if (carry > 0) beatLoadoutData[allocation.productId] = (beatLoadoutData[allocation.productId] || 0) + carry;
              }
            });
          }
          // add plannedLoads in planning state
          if (vehicleStatus === 'planning' && statusData.plannedLoads) {
            Object.entries(statusData.plannedLoads).forEach(([pid, qty]) => {
              const q = Number(qty) || 0;
              if (q > 0) beatLoadoutData[pid] = (beatLoadoutData[pid] || 0) + q;
            });
          }
        });
      }
      
  setRouteIssuingAllocations({ [currentRoute.routeId]: beatLoadoutData });
      
    } catch (error) {
      console.error('Error loading vehicle_daily_status data:', error);
    }
  };

  const loadAllData = async () => {
    // This function is kept for compatibility but replaced by vehicle-based loading
    await loadInitialData();
  };

  const loadInventory = async () => {
    try {
      // Simplified: this function is unused in route-first overview only
      const snapshot = await getDocs(
        query(collection(db, 'inventory'), where('date', '==', selectedDate))
      );
      const inventoryData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as EnhancedInventory));
      
      setInventory(inventoryData);
    } catch (error) {
      console.error('Error loading inventory:', error);
    }
  };

  const loadProducts = async () => {
    try {
      const snapshot = await getDocs(
        query(collection(db, 'products'), orderBy('name'))
      );
      const productsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as EnhancedProduct));
      
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const loadJournalEntries = async () => {
    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'inventory_journal'),
          orderBy('datetime', 'desc')
        )
      );
      const journalData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as InventoryJournal));
      
      setJournalEntries(journalData.slice(0, 50)); // Latest 50 entries
    } catch (error) {
      console.error('Error loading journal entries:', error);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'zoho_sync_status'),
          orderBy('createdAt', 'desc')
        )
      );
      const syncData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
  } as ZohoSyncStatus));
      
      setSyncStatus(syncData.slice(0, 20)); // Latest 20 entries
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  // Handle loadout confirmation with inventory transfer (using UnifiedInventoryTransactionService)
  const handleConfirmLoadout = async () => {
    try {
      setConfirmLoading(true);

      // Resolve today's active beat for this salesman to identify the assigned route and vehicle
      const beatsQuery = query(
        collection(db, 'beats'),
        where('salesmanId', '==', currentUser?.id || ''),
        where('forDate', '==', selectedDate),
        where('isActive', '==', true)
      );
      const beatsSnapshot = await getDocs(beatsQuery);
      if (beatsSnapshot.empty) {
        alert('No active beat found for today.');
        return;
      }
      const beat = beatsSnapshot.docs[0].data() as any;
      const assignedVehicleId = beat.vehicleId as string | undefined;
      const assignedRouteId = beat.routeId as string | undefined; // custom routeId (backfilled)

      if (!assignedRouteId) {
        alert('No route assigned for today.');
        return;
      }
      if (!assignedVehicleId) {
        alert('No vehicle assigned to your route.');
        return;
      }

      // Find issuing for this route
      const routeIssuingQuery = query(
        collection(db, 'route_daily_status'),
        where('routeId', '==', assignedRouteId),
        where('date', '==', selectedDate),
        where('status', '==', 'issuing')
      );
      const issuingSnapshot = await getDocs(routeIssuingQuery);
      if (issuingSnapshot.empty) {
        alert('No pending route loadout found for confirmation!');
        return;
      }

      let totalItemsTransferred = 0;
      for (const docSnapshot of issuingSnapshot.docs) {
        const routeStatusData = docSnapshot.data() as any;

        const parentWarehouseId = routeStatusData.warehouseId || '';
        if (!parentWarehouseId) {
          throw new Error('Route status is missing warehouseId');
        }
        if (!routeStatusData.detailedAllocations || routeStatusData.detailedAllocations.length === 0) {
          throw new Error('No detailed allocation data found. Please re-issue the loadout.');
        }

        const transactionItems = (routeStatusData.detailedAllocations as any[]).map((allocation: any) => {
          const qty = (allocation.issuedQty ?? allocation.allocatedQty) || 0;
          totalItemsTransferred += qty;
          // We may not have productName/unit in minimal payload; fallback sensibly
          const productMeta = (routeProducts || []).find(p => p.productId === allocation.productId);
          return {
            productId: allocation.productId,
            productName: productMeta?.productName || allocation.productName || allocation.productId,
            unit: productMeta?.unit || allocation.unit || 'KG',
            quantity: qty,
            rate: allocation.rate || 0
          };
        });

        // Move stock: from main warehouse (custom ID) to route (custom ID)
        const result = await UnifiedInventoryTransactionService.processLoadoutTransaction(
          parentWarehouseId,
          assignedRouteId,
          transactionItems,
          routeStatusData.beatId || docSnapshot.id,
          currentUser?.id || ''
        );
        if (!result.success) {
          throw new Error(result.errors?.[0] || 'Loadout transaction failed');
        }

        // Mark route as on_field after confirmation
        await updateDoc(docSnapshot.ref, {
          status: 'on_field',
          confirmedAt: serverTimestamp(),
          confirmedBy: currentUser?.id || '',
          salesmanId: currentUser?.id || '',
          totalItemsLoaded: totalItemsTransferred,
          vehicleCustomId: assignedVehicleId // kept for UI labeling only
        });

        // Audit trail (beat_loadout)
        const finalBeatLoadoutRef = doc(collection(db, 'beat_loadout'));
        await setDoc(finalBeatLoadoutRef, {
          beatId: routeStatusData.beatId || `BEAT-${Date.now()}-${assignedVehicleId.slice(-4)}`,
          salesmanId: currentUser?.id || '',
          salesmanName: currentUser?.name || currentUser?.email || 'Salesman',
          vehicleId: assignedVehicleId,
          vehicleNumber: assignedVehicleId,
          parentWarehouseId: parentWarehouseId,
          parentWarehouseName: routeStatusData.warehouseName || 'Warehouse',
          routeId: assignedRouteId,
          routeName: routeStatusData.routeName || assignedRouteId,
          loadoutDate: selectedDate,
          allocations: (routeStatusData.detailedAllocations as any[]).map((a: any) => ({
            // Map minimal + legacy to full BeatLoadout allocation for audit
            productId: a.productId,
            productName: (routeProducts || []).find(p => p.productId === a.productId)?.productName || a.productName || a.productId,
            unit: (routeProducts || []).find(p => p.productId === a.productId)?.unit || a.unit || 'KG',
            availableStock: 0,
            allocatedQty: (a.issuedQty ?? a.allocatedQty) || 0,
            rate: a.rate || 0,
            totalValue: ((a.issuedQty ?? a.allocatedQty) || 0) * (a.rate || 0),
            openingQty: a.openingQty
          })),
          totalValue: routeStatusData.totalValue || 0,
          totalWeight: routeStatusData.totalWeight || 0,
          status: 'loaded',
          issuedAt: routeStatusData.issuedAt,
          loadedAt: serverTimestamp(),
          syncedWithZoho: false,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: routeStatusData.createdBy || '',
          updatedBy: currentUser?.id || ''
        } as Omit<BeatLoadout, 'id'>);
      }

      await loadRouteData();
      alert(`✅ Loadout confirmed successfully!\n\n${totalItemsTransferred} product(s) transferred to your vehicle. You can now conduct sales transactions.`);
    } catch (error) {
      console.error('Confirmation failed:', error);
      alert(`Failed to confirm loadout: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`);
    } finally {
      setConfirmLoading(false);
    }
  };

  // Get vehicle inventory data based on current state
  const getRouteInventoryData = (productId: string) => {
    if (!currentRoute || !currentRoute.routeId) return 0;

    const vehicleStatus = routeStates[currentRoute.routeId];

    switch (vehicleStatus) {
      case 'planning':
        // Show carry-forward + planned loads for expected stock view
        return (routeIssuingAllocations[currentRoute.routeId]?.[productId] || 0);

      case 'issuing': 
        // Data from detailedAllocations (issuedQty)
        return routeIssuingAllocations[currentRoute.routeId]?.[productId] || 0;

      case 'on_field':
        // 🔧 CRITICAL FIX: Data from vehicleInventory (confirmed inventory after loadout)
        return routeInventory[currentRoute.routeId]?.[productId] || 0;

      case 'settled':
        // Data from vehicleInventory (inventory collection)
        return routeInventory[currentRoute.routeId]?.[productId] || 0;

      default:
        return 0;
    }
  };

  // Get current vehicle status
  const getCurrentRouteStatus = () => {
  if (!currentRoute || !currentRoute.routeId) return null;
  return routeStates[currentRoute.routeId] || 'planning';
  };

  // Filter vehicle products based on search - During planning, only show non-zero (carry-forward + planned)
  const getFilteredRouteProducts = () => {
    if (!currentRoute || !currentRoute.routeId) return [];

    const vehicleStatus = getCurrentRouteStatus();

    return routeProducts.filter(product => {
      const quantity = getRouteInventoryData(product.productId);
      const matchesSearch = product.productName.toLowerCase().includes(searchTerm.toLowerCase());

      // During planning stage, only show products with non-zero (carry-forward + planned)
      if (vehicleStatus === 'planning') {
        const hasQuantity = quantity > 0;
        return hasQuantity && matchesSearch;
      }

      // For other stages, only show products with quantities
      const hasQuantity = quantity > 0;
      return hasQuantity && matchesSearch;
    });
  };

  const renderOverview = () => {
    const vehicleStatus = getCurrentRouteStatus();
    const vehicleProducts = getFilteredRouteProducts();
    
    // Show vehicle assignment status if no vehicle
    if (!currentRouteId || !currentRoute) {
      return (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6 text-center">
              <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-800 mb-2">No Route Assigned</h3>
              <p className="text-gray-600">You don't have a route assigned for today.</p>
              <p className="text-sm text-gray-500 mt-2">Please contact your supervisor to get assigned to a route.</p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Available Inventory */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-medium text-gray-800">Available Inventory</h3>
            <p className="text-sm text-gray-600">
              {vehicleStatus === 'planning' ? 'Planned stock (pending warehouse confirmation)' :
               vehicleStatus === 'issuing' ? 'Issued stock (pending your confirmation)' :
               'Confirmed stock in your vehicle'}
            </p>
          </div>
          
          <div className="p-4">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Mobile-friendly table */}
            <div className="space-y-3">
              {vehicleProducts.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600">No inventory data available</p>
                  <p className="text-sm text-gray-500">
                    {vehicleStatus === 'planning' ? 'Waiting for warehouse to plan your loadout' :
                     vehicleStatus === 'issuing' ? 'Check back after confirming your loadout' :
                     'No products in your vehicle'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-600">
                        <th className="py-2 pr-3">Product</th>
                        <th className="py-2 pr-3">Carry Fwd</th>
                        <th className="py-2 pr-3">Loaded</th>
                        <th className="py-2 pr-3">Sold</th>
                        <th className="py-2 pr-3">Replaced</th>
                        <th className="py-2">Current</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vehicleProducts.map((product) => {
                        const quantity = getRouteInventoryData(product.productId);
                        // Approximations: carry-forward from planning allocations (openingQty), loaded from issued/planned, sold/replaced require aggregation (TODO)
                        const carryFwd = routeIssuingAllocations[currentRoute!.routeId]?.[product.productId] && vehicleStatus==='planning' ? routeIssuingAllocations[currentRoute!.routeId][product.productId] : 0;
                        const loaded = vehicleStatus==='issuing' ? (routeIssuingAllocations[currentRoute!.routeId]?.[product.productId] || 0) : (vehicleStatus==='planning' ? (routeLoadData[currentRoute!.routeId]?.[product.productId] || 0) : 0);
                        const sold = 0; // TODO: compute from sales for today
                        const replaced = 0; // TODO: compute from sales replace qty today
                        const current = quantity;
                        return (
                          <tr key={product.productId} className="border-t border-gray-100">
                            <td className="py-2 pr-3">
                              <div className="font-medium text-gray-800">{product.productName}</div>
                              <div className="text-xs text-gray-500">{product.unit}</div>
                            </td>
                            <td className="py-2 pr-3">{carryFwd}</td>
                            <td className="py-2 pr-3">{loaded}</td>
                            <td className="py-2 pr-3">{sold}</td>
                            <td className="py-2 pr-3">{replaced}</td>
                            <td className="py-2">{current}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                /* Old card grid retained for reference
                vehicleProducts.map((product) => {
                  const quantity = getRouteInventoryData(product.productId);
                  
                  // Enhanced status display for planning stage
                  const getStatusDisplay = () => {
                    if (vehicleStatus === 'planning') {
                      if (quantity > 0) {
                        return {
                          text: `Planned: ${quantity}`,
                          subtext: 'Pending Confirmation',
                          color: 'text-blue-600',
                          bgColor: 'bg-blue-50'
                        };
                      } else {
                        return {
                          text: 'Planning...',
                          subtext: 'Warehouse Planning',
                          color: 'text-gray-600',
                          bgColor: 'bg-gray-50'
                        };
                      }
                    } else if (vehicleStatus === 'issuing') {
                      return {
                        text: `Issued: ${quantity}`,
                        subtext: 'Confirm to Load',
                        color: 'text-orange-600',
                        bgColor: 'bg-orange-50'
                      };
                    } else {
                      return {
                        text: quantity.toString(),
                        subtext: 'Available',
                        color: quantity === 0 ? 'text-red-600' : quantity <= 10 ? 'text-orange-600' : 'text-green-600',
                        bgColor: 'bg-gray-50'
                      };
                    }
                  };
                  
                  const statusDisplay = getStatusDisplay();
                  
                  return (
                    <div key={product.productId} className={`flex items-center justify-between p-3 rounded-lg ${statusDisplay.bgColor} border ${
                      vehicleStatus === 'planning' && quantity > 0 ? 'border-blue-200' :
                      vehicleStatus === 'issuing' ? 'border-orange-200' :
                      'border-gray-200'
                    }`}>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-800">{product.productName}</h4>
                        <p className="text-sm text-gray-600">{product.category} • {product.unit}</p>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${statusDisplay.color}`}>
                          {vehicleStatus === 'planning' && quantity === 0 ? (
                            <div className="flex items-center space-x-1">
                              <Clock className="w-4 h-4" />
                              <span className="text-sm">Planning</span>
                            </div>
                          ) : (
                            statusDisplay.text
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {statusDisplay.subtext}
                        </div>
                      </div>
                    </div>
                  );
                })*/
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderJournal = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-800">Inventory Journal</h3>
          <p className="text-sm text-gray-600">Complete audit trail of all stock movements</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {journalEntries.map((entry) => (
            <div key={entry.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      entry.transactionType === 'warehouse_to_vehicle' ? 'bg-blue-100 text-blue-800' :
                      entry.transactionType === 'vehicle_to_retailer' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {entry.transactionType.replace('_', ' → ')}
                    </span>
                    {entry.syncedWithZoho && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                  </div>
                  <h4 className="font-medium text-gray-800">{entry.productName}</h4>
                  <p className="text-sm text-gray-600">
                    {entry.sourceWarehouseName}
                    {entry.destinationWarehouseName && ` → ${entry.destinationWarehouseName}`}
                    {entry.retailerName && ` → ${entry.retailerName}`}
                  </p>
                  {entry.notes && (
                    <p className="text-xs text-gray-500 mt-1">{entry.notes}</p>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-800">{entry.quantity}</div>
                  <div className="text-xs text-gray-500">{entry.unit}</div>
                  {entry.totalValue && (
                    <div className="text-sm text-gray-600">₹{entry.totalValue}</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSyncStatus = () => (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-medium text-gray-800">ERP Sync Status</h3>
          <p className="text-sm text-gray-600">Real-time sync operation status</p>
        </div>
        
        <div className="divide-y divide-gray-200">
          {syncStatus.map((status) => (
            <div key={status.id} className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-medium text-gray-800 capitalize">
                      {status.entityType}
                    </span>
                    <span className="text-xs text-gray-500">
                      {status.syncType}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">ID: {status.entityId}</p>
                  {status.errorMessage && (
                    <p className="text-xs text-red-600 mt-1">{status.errorMessage}</p>
                  )}
                </div>
                <div className="text-right">
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    status.status === 'success' ? 'bg-green-100 text-green-800' :
                    status.status === 'failed' ? 'bg-red-100 text-red-800' :
                    status.status === 'retry' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {status.status}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">
                    Attempts: {status.attempts}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="mobile-container bg-gray-50 min-h-screen">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Package className="w-12 h-12 text-gray-400 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">Loading inventory data...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mobile-container bg-gray-50 min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="p-4">
          <div className="flex items-center space-x-3 mb-4">
            <Package className="w-6 h-6 text-blue-600" />
            <h1 className="text-lg font-semibold text-gray-800">Inventory Management</h1>
          </div>
          
          {/* Date picker only */}
          <div className="flex space-x-2 mb-4">
            <div className="flex-1">
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="p-4 pb-20">
  {activeTab === 'overview' && renderOverview()}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 max-w-md mx-auto">
        <div className="flex justify-around py-2">
          <button 
            onClick={onBack}
            className="flex flex-col items-center py-2 px-4 text-gray-400"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <span className="text-xs mt-1">Home</span>
          </button>
          <button 
            className="flex flex-col items-center py-2 px-4 text-blue-600"
          >
            <Package className="w-6 h-6" />
            <span className="text-xs mt-1">My Stock</span>
          </button>
          <button 
            onClick={onBack}
            className="flex flex-col items-center py-2 px-4 text-gray-400"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 13l-6-3" />
              </svg>
            </div>
            <span className="text-xs mt-1">Routes</span>
          </button>
          <button 
            onClick={onBack}
            className="flex flex-col items-center py-2 px-4 text-gray-400"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <span className="text-xs mt-1">Outlets</span>
          </button>
        </div>
      </nav>
    </div>
  );
};

export default InventoryManagement;
